import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText, Calendar, AlertCircle, ExternalLink, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';

const Reports = () => {
  const navigate = useNavigate();
  const userTier = localStorage.getItem('userTier') || 'pro';
  const [showCustomReports, setShowCustomReports] = useState(false);
  const [customReport, setCustomReport] = useState({
    type: 'income',
    startDate: '',
    endDate: '',
    serviceType: 'all',
    format: 'csv'
  });

  // Helper to generate mock CSV
  const generateCSV = (reportType) => {
    let csvContent = '';
    let filename = '';

    switch (reportType) {
      case 'year-end':
        csvContent = [
          'Report Type,Year-End Tax Summary',
          'Generated,' + new Date().toLocaleDateString(),
          '',
          'Year,Total Revenue,Total Sessions,Avg per Session,Processing Fees,Net Income',
          '2025,$28400,142,$200,$852,$27548',
          '',
          'Revenue by Service',
          'Service,Sessions,Revenue',
          'Tarot Reading,68,$13600',
          'Energy Healing,42,$10080',
          'Life Coaching,32,$4720',
          '',
          'Monthly Breakdown',
          'Month,Revenue',
          'January,$2400',
          'February,$2200',
          'March,$2600',
          'April,$2300',
          'May,$2500',
          'June,$2400',
          'July,$2100',
          'August,$2300',
          'September,$2200',
          'October,$2500',
          'November,$2600',
          'December,$2400'
        ].join('\n');
        filename = 'DF_YearEnd_Tax_Summary_2025.csv';
        break;

      case 'last-year':
        csvContent = [
          'Report Type,Year-End Tax Summary',
          'Generated,' + new Date().toLocaleDateString(),
          '',
          'Year,Total Revenue,Total Sessions,Avg per Session,Processing Fees,Net Income',
          '2024,$24200,121,$200,$726,$23474',
          '',
          'Revenue by Service',
          'Service,Sessions,Revenue',
          'Tarot Reading,58,$11600',
          'Energy Healing,38,$9120',
          'Life Coaching,25,$3480',
          '',
          'Monthly Breakdown',
          'Month,Revenue',
          'January,$1800',
          'February,$1900',
          'March,$2000',
          'April,$2100',
          'May,$2000',
          'June,$2100',
          'July,$1900',
          'August,$2000',
          'September,$2100',
          'October,$2200',
          'November,$2100',
          'December,$2000'
        ].join('\n');
        filename = 'DF_YearEnd_Tax_Summary_2024.csv';
        break;

      case 'q4':
        csvContent = [
          'Report Type,Quarterly Tax Summary',
          'Quarter,Q4 2025',
          'Generated,' + new Date().toLocaleDateString(),
          '',
          'Total Income,$7200',
          'Total Sessions,36',
          'Average per Session,$200',
          '',
          'Revenue by Service',
          'Service,Sessions,Revenue',
          'Tarot Reading,18,$3600',
          'Energy Healing,11,$2640',
          'Life Coaching,7,$960',
          '',
          'Note: Estimated tax payment due January 15 2026'
        ].join('\n');
        filename = 'DF_Quarterly_Summary_Q4_2025.csv';
        break;

      case 'q3':
        csvContent = [
          'Report Type,Quarterly Tax Summary',
          'Quarter,Q3 2025',
          'Generated,' + new Date().toLocaleDateString(),
          '',
          'Total Income,$6600',
          'Total Sessions,33',
          'Average per Session,$200',
          '',
          'Revenue by Service',
          'Service,Sessions,Revenue',
          'Tarot Reading,16,$3200',
          'Energy Healing,10,$2400',
          'Life Coaching,7,$1000',
          '',
          'Note: Estimated tax payment was due September 15 2025'
        ].join('\n');
        filename = 'DF_Quarterly_Summary_Q3_2025.csv';
        break;

      case 'income':
        csvContent = [
          'Date,Client,Service,Amount,Payment Method,Status',
          '2025-12-15,Sarah Martinez,Tarot Reading,80,Credit Card,Completed',
          '2025-12-14,Michael Roberts,Energy Healing,120,Credit Card,Completed',
          '2025-12-13,Luna Starling,Life Coaching,100,Credit Card,Completed',
          '2025-12-12,James Chen,Tarot Reading,80,Credit Card,Completed',
          '2025-12-10,Emma Wilson,Energy Healing,120,Credit Card,Completed',
          '',
          'Total,$500'
        ].join('\n');
        filename = `DF_Income_Report_${customReport.startDate || 'custom'}_to_${customReport.endDate || 'custom'}.csv`;
        break;

      case 'payments':
        csvContent = [
          'Date Paid Out,Gross Amount,Stripe Fee,Net Deposited,Bank Account',
          '2025-12-15,$2400,$72,$2328,****1234',
          '2025-12-08,$2200,$66,$2134,****1234',
          '2025-12-01,$2300,$69,$2231,****1234',
          '',
          'Total Gross,$6900',
          'Total Fees,$207',
          'Total Net,$6693'
        ].join('\n');
        filename = `DF_Payment_History_${customReport.startDate || 'custom'}_to_${customReport.endDate || 'custom'}.csv`;
        break;

      case 'sessions':
        csvContent = [
          'Date,Time,Client,Service,Duration,Amount,Status',
          '2025-12-15,2:00 PM,Sarah Martinez,Tarot Reading,60,$80,Completed',
          '2025-12-14,10:00 AM,Michael Roberts,Energy Healing,90,$120,Completed',
          '2025-12-13,3:00 PM,Luna Starling,Life Coaching,60,$100,Completed',
          '2025-12-12,1:00 PM,James Chen,Tarot Reading,60,$80,Completed',
          '2025-12-10,11:00 AM,Emma Wilson,Energy Healing,90,$120,Completed',
          '',
          'Total Sessions,5',
          'Total Revenue,$500'
        ].join('\n');
        filename = `DF_Session_Report_${customReport.startDate || 'custom'}_to_${customReport.endDate || 'custom'}.csv`;
        break;

      case 'clients':
        csvContent = [
          'Client,Total Sessions,Total Revenue,First Session,Last Session,Status',
          'Sarah Martinez,8,$640,2025-11-15,2025-12-15,Active',
          'Michael Roberts,5,$450,2025-10-20,2025-12-14,Active',
          'Luna Starling,3,$240,2025-12-01,2025-12-13,Active',
          'James Chen,12,$960,2025-08-10,2025-12-12,Active',
          'Emma Wilson,6,$540,2025-09-15,2025-12-10,Active',
          '',
          'Total Clients,5',
          'Total Revenue,$2830'
        ].join('\n');
        filename = `DF_Client_Summary_${customReport.startDate || 'custom'}_to_${customReport.endDate || 'custom'}.csv`;
        break;

      default:
        csvContent = 'No data';
        filename = 'report.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleQuickReport = (type) => {
    generateCSV(type);
  };

  const handleCustomReport = () => {
    // Check if Pro/Elite for PDF
    if (customReport.format === 'pdf' && userTier === 'free') {
      alert('PDF exports require Pro or Elite subscription. Upgrade to unlock!');
      return;
    }

    // Validate dates
    if (!customReport.startDate || !customReport.endDate) {
      alert('Please select start and end dates');
      return;
    }

    generateCSV(customReport.type);
  };

  // Generate Complete Tax Package Excel file with multiple sheets
  const generateCompletePackage = () => {
    // Check tier
    if (userTier === 'free') {
      alert('Complete Tax Package is available for Pro and Elite subscribers. Upgrade to download all your tax reports in one professional Excel file.');
      return;
    }

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Sheet 1 - Summary
    const summaryData = [
      ['Year-End Tax Summary 2025'],
      ['Generated:', new Date().toLocaleDateString()],
      [],
      ['Year', 'Total Revenue', 'Total Sessions', 'Avg per Session', 'Processing Fees', 'Net Income'],
      [2025, 28400, 142, 200, 852, 27548],
      [],
      ['Revenue by Service'],
      ['Service', 'Sessions', 'Revenue'],
      ['Tarot Reading', 68, 13600],
      ['Energy Healing', 42, 10080],
      ['Life Coaching', 32, 4720],
      [],
      ['Monthly Breakdown'],
      ['Month', 'Revenue'],
      ['January', 2400],
      ['February', 2200],
      ['March', 2600],
      ['April', 2300],
      ['May', 2500],
      ['June', 2400],
      ['July', 2100],
      ['August', 2300],
      ['September', 2200],
      ['October', 2500],
      ['November', 2600],
      ['December', 2400]
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    wsSummary['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Sheet 2 - Income
    const incomeData = [
      ['Date', 'Client', 'Service', 'Amount', 'Payment Method', 'Status'],
      ['2025-12-15', 'Sarah Martinez', 'Tarot Reading', 80, 'Credit Card', 'Completed'],
      ['2025-12-14', 'Michael Roberts', 'Energy Healing', 120, 'Credit Card', 'Completed'],
      ['2025-12-13', 'Luna Starling', 'Life Coaching', 100, 'Credit Card', 'Completed'],
      ['2025-12-12', 'James Chen', 'Tarot Reading', 80, 'Credit Card', 'Completed'],
      ['2025-12-10', 'Emma Wilson', 'Energy Healing', 120, 'Credit Card', 'Completed'],
      ['2025-12-08', 'David Park', 'Tarot Reading', 80, 'Credit Card', 'Completed'],
      ['2025-12-05', 'Anna Thompson', 'Life Coaching', 100, 'Credit Card', 'Completed'],
      ['2025-12-03', 'Chris Lee', 'Energy Healing', 120, 'Credit Card', 'Completed'],
      ['2025-12-01', 'Rachel Green', 'Tarot Reading', 80, 'Credit Card', 'Completed'],
      ['2025-11-28', 'Mark Johnson', 'Energy Healing', 120, 'Credit Card', 'Completed'],
      [],
      ['', '', 'Total:', 1000, '', '']
    ];
    const wsIncome = XLSX.utils.aoa_to_sheet(incomeData);
    wsIncome['!cols'] = [{ wch: 12 }, { wch: 18 }, { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsIncome, 'Income');

    // Sheet 3 - Payments
    const paymentsData = [
      ['Date Paid Out', 'Gross Amount', 'Stripe Fee', 'Net Deposited', 'Bank Account'],
      ['2025-12-15', 2400, 72, 2328, '****1234'],
      ['2025-12-08', 2200, 66, 2134, '****1234'],
      ['2025-12-01', 2300, 69, 2231, '****1234'],
      ['2025-11-24', 2100, 63, 2037, '****1234'],
      ['2025-11-17', 2400, 72, 2328, '****1234'],
      ['2025-11-10', 2200, 66, 2134, '****1234'],
      [],
      ['Total Gross:', 13600, '', '', ''],
      ['Total Fees:', '', 408, '', ''],
      ['Total Net:', '', '', 13192, '']
    ];
    const wsPayments = XLSX.utils.aoa_to_sheet(paymentsData);
    wsPayments['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, wsPayments, 'Payments');

    // Sheet 4 - Sessions
    const sessionsData = [
      ['Date', 'Time', 'Client', 'Service', 'Duration (min)', 'Amount', 'Status'],
      ['2025-12-15', '2:00 PM', 'Sarah Martinez', 'Tarot Reading', 60, 80, 'Completed'],
      ['2025-12-14', '10:00 AM', 'Michael Roberts', 'Energy Healing', 90, 120, 'Completed'],
      ['2025-12-13', '3:00 PM', 'Luna Starling', 'Life Coaching', 60, 100, 'Completed'],
      ['2025-12-12', '1:00 PM', 'James Chen', 'Tarot Reading', 60, 80, 'Completed'],
      ['2025-12-10', '11:00 AM', 'Emma Wilson', 'Energy Healing', 90, 120, 'Completed'],
      ['2025-12-08', '9:00 AM', 'David Park', 'Tarot Reading', 60, 80, 'Completed'],
      ['2025-12-05', '4:00 PM', 'Anna Thompson', 'Life Coaching', 60, 100, 'Completed'],
      ['2025-12-03', '2:00 PM', 'Chris Lee', 'Energy Healing', 90, 120, 'Completed'],
      ['2025-12-01', '10:00 AM', 'Rachel Green', 'Tarot Reading', 60, 80, 'Completed'],
      ['2025-11-28', '1:00 PM', 'Mark Johnson', 'Energy Healing', 90, 120, 'Completed'],
      [],
      ['Total Sessions:', '', '', '', '', 10, ''],
      ['Total Revenue:', '', '', '', '', 1000, '']
    ];
    const wsSessions = XLSX.utils.aoa_to_sheet(sessionsData);
    wsSessions['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 18 }, { wch: 15 }, { wch: 14 }, { wch: 10 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsSessions, 'Sessions');

    // Sheet 5 - Clients
    const clientsData = [
      ['Client', 'Total Sessions', 'Total Revenue', 'First Session', 'Last Session', 'Status'],
      ['James Chen', 12, 960, '2025-08-10', '2025-12-12', 'Active'],
      ['Sarah Martinez', 8, 640, '2025-11-15', '2025-12-15', 'Active'],
      ['Emma Wilson', 6, 540, '2025-09-15', '2025-12-10', 'Active'],
      ['Michael Roberts', 5, 450, '2025-10-20', '2025-12-14', 'Active'],
      ['Luna Starling', 3, 240, '2025-12-01', '2025-12-13', 'Active'],
      ['David Park', 4, 320, '2025-10-01', '2025-12-08', 'Active'],
      ['Anna Thompson', 3, 300, '2025-11-01', '2025-12-05', 'Active'],
      ['Chris Lee', 2, 240, '2025-11-15', '2025-12-03', 'Active'],
      ['Rachel Green', 2, 160, '2025-11-20', '2025-12-01', 'Active'],
      ['Mark Johnson', 2, 240, '2025-11-10', '2025-11-28', 'Active'],
      [],
      ['Total Clients:', 10, '', '', '', ''],
      ['Total Revenue:', '', 4090, '', '', '']
    ];
    const wsClients = XLSX.utils.aoa_to_sheet(clientsData);
    wsClients['!cols'] = [{ wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, wsClients, 'Clients');

    // Sheet 6 - Notes
    const notesData = [
      ['About This Report'],
      [],
      ['This Excel file contains all your business income data for tax filing.'],
      ['Each sheet contains different aspects of your Divine Frameworks business.'],
      [],
      ['What to Share with Your Accountant:'],
      ['- Summary sheet: Total income for Schedule C Line 1 (Gross receipts)'],
      ['- Income sheet: Detailed transaction records for your records'],
      ['- Payments sheet: Bank deposit reconciliation'],
      ['- Sessions sheet: Service delivery documentation'],
      ['- Clients sheet: Client relationship summary'],
      [],
      ['Important Notes:'],
      ['- All amounts are in USD'],
      ['- Processing fees (Stripe) are deductible business expenses'],
      ['- Keep this file for at least 7 years for tax records'],
      [],
      ['Generated by Divine Frameworks on ' + new Date().toLocaleDateString()],
      ['Questions? support@divineframeworks.com']
    ];
    const wsNotes = XLSX.utils.aoa_to_sheet(notesData);
    wsNotes['!cols'] = [{ wch: 70 }];
    XLSX.utils.book_append_sheet(wb, wsNotes, 'Notes');

    // Write file and download
    XLSX.writeFile(wb, 'DF_Complete_Tax_Package_2025.xlsx');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <span
            onClick={() => navigate('/dashboard/practitioner')}
            className="text-purple-600 hover:text-purple-700 font-medium transition-colors cursor-pointer"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/practitioner'); }}
          >
            Dashboard
          </span>
          <span className="text-gray-400" aria-hidden="true">/</span>
          <span className="text-gray-900 font-medium" aria-current="page">Reports & Exports</span>
        </nav>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Reports & Tax Exports</h1>
          <p className="text-gray-600">Download income reports for tax filing and accounting</p>
        </div>

        {/* Tax Disclaimer */}
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 mb-6">
          <div className="flex gap-4">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Important Tax Information</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                Divine Frameworks provides income reporting tools to help you understand your business performance.
                We are not tax advisors or CPAs. The information provided is for general educational purposes only.
                Please consult a qualified tax professional for advice specific to your situation.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.irs.gov/forms-pubs/about-schedule-c-form-1040"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  IRS Schedule C Guide
                  <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="https://www.irs.gov/businesses/small-businesses-self-employed/self-employment-tax-social-security-and-medicare-taxes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  Self-Employment Tax Guide
                  <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="https://www.irs.gov/businesses/small-businesses-self-employed/estimated-taxes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  Quarterly Tax Payments
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Reports */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Reports</h2>
          <p className="text-sm text-gray-600 mb-4">One-click downloads for common tax reports</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Complete Tax Package - Pro/Elite Only */}
            {userTier !== 'free' && (
              <button
                onClick={generateCompletePackage}
                className="text-left p-6 md:col-span-2 rounded-xl transition-colors bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300 hover:from-purple-100 hover:to-indigo-100"
              >
                <div className="flex items-start gap-4">
                  <div className="rounded-xl p-3 bg-purple-600">
                    <FileSpreadsheet className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-1">Complete Tax Package</h3>
                    <p className="text-sm text-gray-600">
                      All reports in one Excel file (.xlsx) - perfect for tax filing and record-keeping
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <Download className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </button>
            )}

            {/* Year-End Tax Summary */}
            <button
              onClick={() => handleQuickReport('year-end')}
              className="text-left p-5 border-2 border-purple-200 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="bg-purple-600 rounded-lg p-2">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <Download className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Year-End Tax Summary</h3>
              <p className="text-sm text-gray-600">Total income for 2025 (Schedule C)</p>
            </button>

            {/* Last Year */}
            <button
              onClick={() => handleQuickReport('last-year')}
              className="text-left p-5 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="bg-gray-600 rounded-lg p-2">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <Download className="w-5 h-5 text-gray-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Last Year Tax Summary</h3>
              <p className="text-sm text-gray-600">Total income for 2024</p>
            </button>

            {/* Q4 Summary */}
            <button
              onClick={() => handleQuickReport('q4')}
              className="text-left p-5 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="bg-indigo-600 rounded-lg p-2">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <Download className="w-5 h-5 text-gray-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Q4 2025 Summary</h3>
              <p className="text-sm text-gray-600">Oct-Dec income (due Jan 15)</p>
            </button>

            {/* Q3 Summary */}
            <button
              onClick={() => handleQuickReport('q3')}
              className="text-left p-5 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="bg-blue-600 rounded-lg p-2">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <Download className="w-5 h-5 text-gray-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Q3 2025 Summary</h3>
              <p className="text-sm text-gray-600">Jul-Sep income</p>
            </button>
          </div>
        </div>

        {/* Custom Reports */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Custom Reports</h2>
              <p className="text-sm text-gray-600">Generate reports with custom date ranges and filters</p>
            </div>
            <button
              onClick={() => setShowCustomReports(!showCustomReports)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              {showCustomReports ? 'Hide' : 'Show'} Builder
            </button>
          </div>

          {showCustomReports && (
            <div className="border-t border-gray-200 pt-6 space-y-4">
              {/* Report Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                <select
                  value={customReport.type}
                  onChange={(e) => setCustomReport({ ...customReport, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="income">Income Report</option>
                  <option value="payments">Payment History</option>
                  <option value="sessions">Session Report</option>
                  <option value="clients">Client Summary</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={customReport.startDate}
                    onChange={(e) => setCustomReport({ ...customReport, startDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={customReport.endDate}
                    onChange={(e) => setCustomReport({ ...customReport, endDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Service Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
                <select
                  value={customReport.serviceType}
                  onChange={(e) => setCustomReport({ ...customReport, serviceType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">All Services</option>
                  <option value="tarot">Tarot Reading</option>
                  <option value="energy">Energy Healing</option>
                  <option value="coaching">Life Coaching</option>
                </select>
              </div>

              {/* Format */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="csv"
                      checked={customReport.format === 'csv'}
                      onChange={(e) => setCustomReport({ ...customReport, format: e.target.value })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm text-gray-700">CSV (All tiers)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="pdf"
                      checked={customReport.format === 'pdf'}
                      onChange={(e) => setCustomReport({ ...customReport, format: e.target.value })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm text-gray-700">
                      PDF {userTier === 'free' && '(Pro/Elite only)'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleCustomReport}
                className="w-full px-5 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Generate Report
              </button>
            </div>
          )}
        </div>

        {/* Tier Upgrade Prompt - Free Users */}
        {userTier === 'free' && (
          <div className="mt-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <span className="text-4xl">📊</span>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-2">Upgrade for PDF Reports</h3>
                <p className="text-gray-700 mb-4">
                  Pro and Elite tiers include professionally formatted PDF exports,
                  perfect for sharing with your accountant or keeping organized records.
                </p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                >
                  View Plans
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
